import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import OrganizationIcon from '@mui/icons-material/LanOutlined';

import { customerService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import { languages } from '../../../components/LanguageSelector';
import { CustomerStatusChip } from './columns';
import { formatDate, formatTaxId } from '../../../utils/formatters';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';

const PRIMARY_PROPS = { variant: 'body2', color: 'text.secondary' };
const SECONDARY_PROPS = { variant: 'body1', color: 'text.primary' };

function Field({ label, value, divider = true }) {
  return (
    <ListItem disableGutters divider={divider}>
      <ListItemText
        primary={label}
        secondary={value || '—'}
        primaryTypographyProps={PRIMARY_PROPS}
        secondaryTypographyProps={typeof value === 'string' || !value ? SECONDARY_PROPS : { component: 'div' }}
      />
    </ListItem>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { collaborator } = useAuth();
  const isInternal = Boolean(collaborator?.is_internal);
  const [flashMessage, setFlashMessage] = useState('');

  const customerQuery = useQuery({
    queryKey: ['customers', id],
    retry: retryOnServerError,
    queryFn: () => customerService.getCustomer(id).then((res) => res.data),
  });

  const customer = customerQuery.data;

  const handleDelete = async () => {
    await customerService.deleteCustomer(id);
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    navigate('/customers', {
      state: { seedDelete: { ids: [id], message: t('customers.deleteSuccess') } },
    });
  };

  const handleRestore = async () => {
    await customerService.restoreCustomer(id);
    await queryClient.invalidateQueries({ queryKey: ['customers'] });
    setFlashMessage(t('customers.restoreSuccess'));
  };

  const header = (
    <PageHeader
      title={customer?.display_name ?? t('customers.detailTitle')}
      breadcrumbs={[
        { label: t('customers.breadcrumb.dashboard') },
        { label: t('nav.groups.admin') },
        { label: t('customers.breadcrumb.list'), to: '/customers' },
        { label: customer?.display_name ?? t('customers.detailTitle') },
      ]}
    />
  );

  const renderBody = () => {
    if (customerQuery.isLoading) {
      return <LoadingState message={t('customers.states.loading')} />;
    }

    if (customerQuery.isError) {
      const status = customerQuery.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('customers.states.permissionDeniedTitle')}
            message={t('customers.states.permissionDeniedMessage')}
          />
        );
      }
      if (status === 404) {
        return (
          <ErrorState title={t('customers.states.notFoundTitle')} message={t('customers.states.notFoundMessage')} />
        );
      }
      return (
        <ErrorState
          title={t('customers.states.errorTitle')}
          message={t('customers.states.errorMessage')}
          actionLabel={t('customers.states.retry')}
          onRetry={customerQuery.refetch}
        />
      );
    }

    const persona = customer.persona ?? {};
    const businesses = customer.businesses ?? [];

    return (
      <Stack spacing={3} sx={{ width: '100%', pb: 3 }}>
        <Card>
          <CardHeader
            title={t('customers.sections.identification')}
            subheader={t('customers.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <Field label={t('customers.fields.displayName')} value={customer.display_name} />
              <Field label={t('customers.fields.provider')} value={customer.provider_name} />
              <Field
                label={t('customers.fields.language')}
                value={
                  languages.find((option) => option.code === customer.language)?.label ??
                  t('customers.fields.languageNone')
                }
              />
              <Field label={t('customers.fields.createdAt')} value={formatDate(customer.created_at, i18n.language)} />
              <Field
                label={t('customers.fields.status')}
                value={<CustomerStatusChip t={t} customer={customer} sx={{ mt: 0.5 }} />}
                divider={false}
              />
            </List>
          </CardContent>
          {isInternal && (
            <>
              <Divider />
              <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
                {customer.is_deleted ? (
                  <Button variant="contained" startIcon={<RestoreIcon />} onClick={handleRestore}>
                    {t('customers.actions.restore')}
                  </Button>
                ) : (
                  <>
                    <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete}>
                      {t('customers.actions.delete')}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => navigate(`/customers/${id}/edit`)}
                    >
                      {t('customers.actions.edit')}
                    </Button>
                  </>
                )}
              </CardActions>
            </>
          )}
        </Card>

        <Card>
          <CardHeader
            title={t('customers.sections.company')}
            subheader={t('customers.sections.companyHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <Field label={t('customers.fields.fullName')} value={persona.full_name} />
              <Field label={t('customers.fields.legalName')} value={persona.legal_name} />
              <Field label={t('customers.fields.taxId')} value={formatTaxId(persona.tax_id, persona.country)} />
              <Field label={t('customers.fields.registrationNumber')} value={persona.registration_number} />
              <Field label={t('customers.fields.email')} value={persona.email} />
              <Field label={t('customers.fields.website')} value={persona.website} />
              <Field label={t('customers.fields.country')} value={persona.country} />
              <Field
                label={t('customers.fields.companyType')}
                value={persona.company_type && t(`customers.companyTypes.${persona.company_type}`)}
                divider={false}
              />
            </List>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('customers.sections.businesses')}
            subheader={t('customers.sections.businessesHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: businesses.length ? 0 : 2 }}>
            {businesses.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('customers.noBusinesses')}
              </Typography>
            ) : (
              <List disablePadding>
                {businesses.map((business, index) => (
                  <ListItem key={business.uuid} disableGutters disablePadding divider={index < businesses.length - 1}>
                    <ListItemButton component={RouterLink} to={`/businesses/${business.uuid}`} sx={{ px: 0 }}>
                      <ListItemText primary={business.name} primaryTypographyProps={{ color: 'primary' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            {businesses.length > 0 && (
              <Button component={RouterLink} to={`/businesses?customer=${id}`}>
                {t('customers.actions.viewAllBusinesses')}
              </Button>
            )}
            <Button startIcon={<OrganizationIcon />} component={RouterLink} to={`/organization?customer=${id}`}>
              {t('customers.actions.viewOrganization')}
            </Button>
            {/* Adding stores depends on the contract: internal users only. */}
            {isInternal && !customer.is_deleted && (
              <Button variant="outlined" startIcon={<AddIcon />} component={RouterLink} to={`/businesses/new?customer=${id}`}>
                {t('customers.actions.newBusiness')}
              </Button>
            )}
          </CardActions>
        </Card>
      </Stack>
    );
  };

  return (
    <BoundedPageLayout header={header}>
      {renderBody()}
      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
